import { Label, SegmentInline } from '@mairistem/composants';
import { type Exercice, Sens } from '@mairistem/comptabilite';
import { formatMontant, type UserRole } from '@mairistem/finances-utils';
import { isNaN } from 'lodash';
import React from 'react';
import TreeNodeType from '../../../../constants/liquidation/TreeNodeType';
import type {
	BordereauWithState,
	TreeRow,
} from '../../../../interfaces/pieces/TreeRow';
import BordereauAction from './BordereauAction';
import './Bordereau.css';
import { SENS_TOUS } from '../../../../interfaces/pieces/bordereaux/SensOptions';

interface BordereauSectionProps {
	rowData: TreeRow;
	exercice: Exercice;
	expanded: boolean;
	userRole: UserRole;
	filterSens: string;
	onChange: (
		rowData: BordereauWithState,
		shouldRefreshChildren?: boolean,
	) => void;
	onDelete?: (rowData: BordereauWithState) => void;
	onAdd?: (rowData: BordereauWithState) => void;
}

const getSens = (sens: string) => {
	switch (sens) {
		case Sens.DEPENSE:
			return 'Dépense';
		case Sens.RECETTE:
			return 'Recette';
		default:
			return 'Tous';
	}
};

const BordereauSection = ({
	rowData,
	exercice,
	expanded,
	userRole,
	filterSens,
	onChange,
	onDelete,
	onAdd,
}: BordereauSectionProps) => {
	const { bordereau } = rowData;

	if (!bordereau) return null;

	const isNewBordereau = rowData.nodeType === TreeNodeType.CREATE_BORDEREAU;
	const isAnnulation = bordereau.typePes === '02';
	const isRejet = bordereau.typePes === 'RE';

	const labelColor = isAnnulation || isRejet ? 'rejetAnnulationLabelColor' : '';
	const bordereauLabel = isNewBordereau ? rowData.libelle : bordereau.libelle;
	const rejetLabel = isRejet ? 'Rejet' : '';
	const annulationLabel = isAnnulation ? 'Annulation-réduction' : '';

	let montant = '';

	if (!isNewBordereau) {
		const montantValue = parseFloat(bordereau.montant);
		const montantFormate = isNaN(montantValue)
			? formatMontant(0)
			: formatMontant(montantValue);
		montant = `${isAnnulation || isRejet ? '- ' : ''}${montantFormate}`;
	}

	return (
		<SegmentInline
			className="bordereau-section"
			data-testid={
				isNewBordereau
					? 'bordereauRowCreate'
					: `bordereauRow${bordereau.identifiant}`
			}
		>
			<div
				className={`bordereau-section-text ${labelColor} ${expanded && 'bordereau-section-bold'}`}
			>
				<Label
					horizontal
					style={{ backgroundColor: bordereau.state.color }}
					size="medium"
					className="bordereau-label"
				>
					{bordereau.state.libelle}
				</Label>
				<span className="bordereau-section-numero">
					{!isNewBordereau && <>{bordereau.numero} |</>}
				</span>
				<span className="bordereau-section-label">
					{[
						...(filterSens === SENS_TOUS ? [getSens(bordereau.sens)] : []),
						...(annulationLabel ? [annulationLabel] : []),
						...(rejetLabel ? [rejetLabel] : []),
						...(bordereauLabel ? [bordereauLabel] : []),
					].join(' | ')}
					{montant && '  | '}
				</span>
				<span className="bordereau-section-bold">{montant}</span>
			</div>
			<BordereauAction
				bordereau={bordereau}
				exercice={exercice}
				expanded={expanded}
				onChange={onChange}
				onDelete={onDelete}
				onAdd={onAdd}
				userRole={userRole}
			/>
		</SegmentInline>
	);
};

export default BordereauSection;
